import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  FlaskConical, Loader2, Plus, Package, LayoutDashboard, AlertTriangle,
  CheckCircle2, ClipboardList, XCircle, Image as ImageIcon,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nProvider";
import { eur } from "@/lib/constants";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STAGES = ["order_received", "design", "manufacturing", "finishing", "quality_control", "ready_for_delivery", "delivered"];
const STAGE_LABEL = {
  order_received: "Order Received", design: "Design", manufacturing: "Manufacturing",
  finishing: "Finishing", quality_control: "Quality Control", ready_for_delivery: "Ready for Delivery", delivered: "Delivered",
};
const STAGE_STYLE = {
  order_received: "bg-slate-100 text-slate-600", design: "bg-blue-100 text-blue-700",
  manufacturing: "bg-indigo-100 text-indigo-700", finishing: "bg-violet-100 text-violet-700",
  quality_control: "bg-amber-100 text-amber-700", ready_for_delivery: "bg-teal-100 text-teal-700",
  delivered: "bg-emerald-100 text-emerald-700",
};

export default function Laboratory() {
  const { can } = useAuth();
  const t = useT();
  return (
    <div className="p-6 sm:p-8 max-w-[1400px]">
      <PageHeader title={t("lab.title")} subtitle={t("lab.subtitle")} />
      <Tabs defaultValue="orders">
        <TabsList>
          <TabsTrigger value="orders" data-testid="lab-tab-orders"><ClipboardList className="h-4 w-4 mr-2" />{t("lab.orders")}</TabsTrigger>
          <TabsTrigger value="inventory" data-testid="lab-tab-inventory"><Package className="h-4 w-4 mr-2" />{t("lab.inventory")}</TabsTrigger>
          <TabsTrigger value="dashboard" data-testid="lab-tab-dashboard"><LayoutDashboard className="h-4 w-4 mr-2" />{t("lab.dashboard")}</TabsTrigger>
        </TabsList>
        <TabsContent value="orders" className="mt-4"><OrdersTab can={can} /></TabsContent>
        <TabsContent value="inventory" className="mt-4"><InventoryTab can={can} /></TabsContent>
        <TabsContent value="dashboard" className="mt-4"><LabDashboardTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function OrdersTab({ can }) {
  const t = useT();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refs, setRefs] = useState({ patients: [], dentists: [], technicians: [] });
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({
    patient_id: "", responsible_dentist_id: "", restoration_type: "Zirconia Crown",
    tooth_numbers: "", material: "", shade: "", quantity: 1, required_delivery_date: "", expected_completion_date: "",
  });

  const load = useCallback(() => {
    setLoading(true);
    api.get("/lab/orders").then((r) => setOrders(r.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    Promise.all([api.get("/patients"), api.get("/meta/dentists"), api.get("/meta/technicians")])
      .then(([p, d, t]) => setRefs({ patients: p.data, dentists: d.data, technicians: t.data })).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.patient_id) return toast.error(t("lab.selectPatientErr"));
    setSaving(true);
    try {
      await api.post("/lab/orders", {
        ...form, quantity: Number(form.quantity),
        tooth_numbers: form.tooth_numbers.split(",").map((s) => s.trim()).filter(Boolean),
        responsible_dentist_id: form.responsible_dentist_id || null,
      });
      toast.success(t("lab.orderCreated")); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      {can("laboratory", "create") && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-lab-order-button" className="mb-4 bg-navy hover:bg-navy-soft text-white gap-2"><Plus className="h-4 w-4" />{t("lab.newOrder")}</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">{t("lab.newOrderTitle")}</DialogTitle>
              <DialogDescription>{t("lab.newOrderDesc")}</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="lab-order-form">
              <div className="space-y-1.5 col-span-2">
                <Label>{t("lab.selectPatient")} *</Label>
                <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
                  <SelectTrigger data-testid="lab-patient"><SelectValue placeholder={t("lab.selectPatient")} /></SelectTrigger>
                  <SelectContent>{refs.patients.map((p) => (<SelectItem key={p.id} value={p.id}>{p.patient_code} · {p.first_name} {p.last_name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>{t("lab.restorationType")} *</Label><Input value={form.restoration_type} onChange={(e) => setForm({ ...form, restoration_type: e.target.value })} required data-testid="lab-restoration" /></div>
              <div className="space-y-1.5"><Label>{t("lab.toothNumbers")}</Label><Input value={form.tooth_numbers} onChange={(e) => setForm({ ...form, tooth_numbers: e.target.value })} placeholder="11, 21, 22" /></div>
              <div className="space-y-1.5"><Label>{t("lab.material")}</Label><Input value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>{t("lab.shade")}</Label><Input value={form.shade} onChange={(e) => setForm({ ...form, shade: e.target.value })} placeholder="A2" /></div>
              <div className="space-y-1.5"><Label>{t("lab.quantity")}</Label><Input type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>{t("lab.responsibleDentist")}</Label>
                <Select value={form.responsible_dentist_id} onValueChange={(v) => setForm({ ...form, responsible_dentist_id: v })}>
                  <SelectTrigger data-testid="lab-dentist"><SelectValue placeholder={t("common.optional")} /></SelectTrigger>
                  <SelectContent>{refs.dentists.map((d) => (<SelectItem key={d.id} value={d.id}>{d.full_name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>{t("lab.requiredDate")}</Label><Input type="date" value={form.required_delivery_date} onChange={(e) => setForm({ ...form, required_delivery_date: e.target.value })} data-testid="lab-required-date" /></div>
              <div className="space-y-1.5"><Label>{t("lab.expectedCompletion")}</Label><Input type="date" value={form.expected_completion_date} onChange={(e) => setForm({ ...form, expected_completion_date: e.target.value })} data-testid="lab-expected-date" /></div>
              <DialogFooter className="col-span-2"><Button type="submit" data-testid="lab-order-submit" disabled={saving} className="bg-navy hover:bg-navy-soft text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("lab.createOrder")}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {loading ? <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-navy" /></div> : (
        <div className="space-y-3">
          {orders.length === 0 && <div className="bg-white rounded-xl border border-slate-200 p-16 text-center text-slate-400">{t("lab.noOrders")}</div>}
          {orders.map((o) => (
            <button key={o.id} data-testid={`lab-order-${o.order_number}`} onClick={() => setDetail(o)}
              className="w-full text-left bg-white rounded-xl border border-slate-200 p-4 stat-lift hover:border-navy/30">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <span className="font-mono font-semibold text-navy">{o.order_number}</span>
                  <span className="text-slate-700">{o.patient_name}</span>
                  <Badge className={`${STAGE_STYLE[o.status]} hover:${STAGE_STYLE[o.status]} font-normal`}>{t(`lab.stage_${o.status}`, STAGE_LABEL[o.status])}</Badge>
                  {o.delayed && <Badge variant="destructive" className="gap-1"><AlertTriangle className="h-3 w-3" />{t("lab.delayed")}</Badge>}
                  {o.deadline_conflict && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 gap-1"><AlertTriangle className="h-3 w-3" />{t("lab.deadlineConflict")}</Badge>}
                </div>
                <span className="text-sm text-slate-500">{o.restoration_type} · {o.assigned_technician_name || t("lab.unassigned")}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {detail && <OrderDetail order={detail} technicians={refs.technicians} can={can} onClose={() => setDetail(null)} onChanged={(o) => { setDetail(o); load(); }} />}
    </div>
  );
}

function UnitSource({ unit, busy, onSet }) {
  const [mode, setMode] = useState(null); // null | 'external'
  const [vendor, setVendor] = useState(unit.external_vendor || "");
  if (mode === "external") {
    return (
      <span className="flex items-center gap-1">
        <Input value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="External vendor name"
          className="h-7 text-xs w-44" data-testid={`unit-vendor-input-${unit.unit_index}`} />
        <Button size="sm" className="h-7 bg-navy text-white" disabled={busy || !vendor.trim()} data-testid={`unit-source-save-${unit.unit_index}`}
          onClick={() => onSet({ production_source: "external", external_vendor: vendor.trim() })}>Save</Button>
        <Button size="sm" variant="ghost" className="h-7" onClick={() => setMode(null)}>Cancel</Button>
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1">
      <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} data-testid={`unit-source-internal-${unit.unit_index}`}
        onClick={() => onSet({ production_source: "internal" })}>Internal</Button>
      <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`unit-source-external-${unit.unit_index}`}
        onClick={() => setMode("external")}>External…</Button>
    </span>
  );
}

function OrderDetail({ order, technicians, can, onClose, onChanged }) {
  const { user } = useAuth();
  const tr = useT();
  const [o, setO] = useState(order);
  const [busy, setBusy] = useState(false);
  const nextStage = STAGES[STAGES.indexOf(o.status) + 1];
  const canSetSource = ["owner", "operations_manager", "laboratory_coordinator"].includes(user?.role);

  const doCall = async (fn) => {
    setBusy(true);
    try { const { data } = await fn(); setO(data); onChanged(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const assign = (tid) => doCall(() => api.patch(`/lab/orders/${o.id}`, { assigned_technician_id: tid }));
  const advance = () => doCall(() => api.post(`/lab/orders/${o.id}/stage`, { stage: nextStage }));
  const qc = (result) => doCall(() => api.post(`/lab/orders/${o.id}/quality`, { result, notes: result === "reject" ? "Rework required" : "Approved" }));
  const deliver = () => doCall(() => api.post(`/lab/orders/${o.id}/deliver`, {}));
  const viewMedia = async (mediaId) => {
    try {
      const res = await api.get(`/lab/orders/${o.id}/media/${mediaId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error(tr("lab.openPhotoErr")); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="lab-order-detail">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">{o.order_number}
            <Badge className={`${STAGE_STYLE[o.status]} font-normal`}>{tr(`lab.stage_${o.status}`, STAGE_LABEL[o.status])}</Badge>
          </DialogTitle>
          <DialogDescription>{o.patient_name} · {o.restoration_type} · {tr("lab.toothNumbers")} {o.tooth_numbers?.join(", ") || "—"}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div><p className="text-slate-400 text-xs">{tr("lab.material")}</p><p>{o.material || "—"}</p></div>
            <div><p className="text-slate-400 text-xs">{tr("lab.shade")}</p><p>{o.shade || "—"}</p></div>
            <div><p className="text-slate-400 text-xs">{tr("lab.requiredBy")}</p><p>{o.required_delivery_date || "—"}</p></div>
            <div><p className="text-slate-400 text-xs">{tr("lab.expected")}</p><p>{o.expected_completion_date || "—"}</p></div>
            <div><p className="text-slate-400 text-xs">{tr("lab.reworkCount")}</p><p>{o.rework_count}</p></div>
            {"material_cost" in o && <div><p className="text-slate-400 text-xs">{tr("lab.materialCost")}</p><p className="tabular-nums">{eur(o.material_cost)}</p></div>}
          </div>

          {/* Measurement receipt + clinical instructions (Section 4) */}
          <div className="rounded-lg border border-slate-200 p-3 bg-slate-50/60" data-testid="measurement-block">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="text-xs text-slate-400">{tr("lab.measurementWorkflow")}</p>
                <p className="font-medium capitalize">{o.measurement_type ? o.measurement_type.replace(/_/g, " / ") : "—"}</p>
              </div>
              {o.measurement_received ? (
                <Badge className="bg-emerald-100 text-emerald-700 font-normal" data-testid="measurement-received-badge">
                  {tr("lab.received")} · {o.measurement_received_by} · {(o.measurement_received_at || "").slice(0, 16).replace("T", " ")}
                </Badge>
              ) : can("laboratory", "edit") ? (
                <Button size="sm" disabled={busy} data-testid="receive-measurements-button" className="bg-navy text-white"
                  onClick={() => doCall(() => api.post(`/lab/orders/${o.id}/receive-measurements`))}>{tr("lab.measurementsReceived")}</Button>
              ) : <Badge variant="secondary">{tr("common.pending")}</Badge>}
            </div>
            {o.clinical_instructions && (
              <p className="text-sm text-slate-600 mt-2" data-testid="clinical-instructions"><span className="text-slate-400">{tr("lab.instructions")}: </span>{o.clinical_instructions}</p>
            )}
            {o.clinical_media?.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-slate-400 mb-1">{tr("lab.clinicalPhotos")}</p>
                <div className="flex flex-wrap gap-2" data-testid="clinical-media-list">
                  {o.clinical_media.map((m) => (
                    <button key={m.id} data-testid={`view-media-${m.id}`} onClick={() => viewMedia(m.id)}
                      className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1">
                      <ImageIcon className="h-3.5 w-3.5" />{m.original_filename}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {can("laboratory", "edit") && (
            <div className="space-y-1.5">
              <Label>{tr("lab.assignedTechnician")}</Label>
              <Select value={o.assigned_technician_id || ""} onValueChange={assign}>
                <SelectTrigger data-testid="assign-technician"><SelectValue placeholder={tr("lab.assignTechnician")} /></SelectTrigger>
                <SelectContent>{technicians.map((t) => (<SelectItem key={t.id} value={t.id}>{t.full_name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {can("laboratory", "edit") && nextStage && nextStage !== "ready_for_delivery" && nextStage !== "delivered" && (
              <Button size="sm" disabled={busy} onClick={advance} data-testid="advance-stage" className="bg-navy text-white">{tr("lab.advanceTo")} {tr(`lab.stage_${nextStage}`, STAGE_LABEL[nextStage])}</Button>
            )}
            {can("laboratory", "approve") && o.status === "quality_control" && (
              <>
                <Button size="sm" disabled={busy} onClick={() => qc("pass")} data-testid="qc-pass" className="bg-emerald-600 text-white gap-1"><CheckCircle2 className="h-4 w-4" />{tr("lab.qcPass")}</Button>
                <Button size="sm" disabled={busy} onClick={() => qc("reject")} data-testid="qc-reject" variant="outline" className="text-red-600 gap-1"><XCircle className="h-4 w-4" />{tr("lab.qcReject")}</Button>
              </>
            )}
            {can("laboratory", "approve") && o.status === "ready_for_delivery" && o.quality_passed && (
              <Button size="sm" disabled={busy} onClick={deliver} data-testid="deliver-order" className="bg-emerald-600 text-white">{tr("lab.deliverToClinic")}</Button>
            )}
          </div>

          <div>
            <p className="font-medium text-navy mb-2">{tr("lab.crownsUnits")} ({o.units?.length || 0})</p>
            <div className="space-y-2">
              {(o.units || []).map((u) => {
                const un = STAGES[STAGES.indexOf(u.status) + 1];
                return (
                  <div key={u.unit_index} data-testid={`unit-${u.unit_index}`} className="rounded-lg border border-slate-200 p-2.5">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <span className="text-sm font-medium">{tr("lab.unit", "Unit")} {u.unit_index}{u.tooth ? ` · ${tr("lab.toothNumbers")} ${u.tooth}` : ""}</span>
                      <div className="flex items-center gap-2">
                        <Badge className={`${STAGE_STYLE[u.status]} font-normal`}>{tr(`lab.stage_${u.status}`, STAGE_LABEL[u.status])}</Badge>
                        {u.rework_count > 0 && <span className="text-xs text-amber-600">{tr("lab.reworkCount")} ×{u.rework_count}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Badge className={`font-normal ${(u.production_source || "internal") === "external" ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-600"}`} data-testid={`unit-source-${u.unit_index}`}>
                        {(u.production_source || "internal") === "external" ? `${tr("lab.external")}${u.external_vendor ? ` · ${u.external_vendor}` : ""}` : tr("lab.internal")}
                      </Badge>
                      {canSetSource && !u.quality_passed && (
                        <UnitSource unit={u} busy={busy} onSet={(payload) => doCall(() => api.post(`/lab/orders/${o.id}/units/${u.unit_index}/production-source`, payload))} />
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {can("laboratory", "edit") && (
                        <Select value={u.technician_id || ""} onValueChange={(v) => doCall(() => api.patch(`/lab/orders/${o.id}/units/${u.unit_index}/assign`, { technician_id: v }))}>
                          <SelectTrigger className="h-7 text-xs w-40" data-testid={`unit-assign-${u.unit_index}`}><SelectValue placeholder={u.technician_name || tr("lab.assign")} /></SelectTrigger>
                          <SelectContent>{technicians.map((t) => (<SelectItem key={t.id} value={t.id}>{t.full_name}</SelectItem>))}</SelectContent>
                        </Select>
                      )}
                      {can("laboratory", "edit") && un && !["ready_for_delivery", "delivered"].includes(un) && (
                        <Button size="sm" variant="outline" disabled={busy} data-testid={`unit-advance-${u.unit_index}`}
                          onClick={() => doCall(() => api.post(`/lab/orders/${o.id}/units/${u.unit_index}/stage`, { stage: un }))}>→ {tr(`lab.stage_${un}`, STAGE_LABEL[un])}</Button>
                      )}
                      {can("laboratory", "approve") && u.status === "quality_control" && (
                        <>
                          <Button size="sm" disabled={busy} className="bg-emerald-600 text-white h-7" data-testid={`unit-qc-pass-${u.unit_index}`}
                            onClick={() => doCall(() => api.post(`/lab/orders/${o.id}/units/${u.unit_index}/quality`, { result: "pass" }))}>{tr("lab.qcPass")}</Button>
                          <Button size="sm" variant="outline" disabled={busy} className="text-red-600 h-7" data-testid={`unit-qc-reject-${u.unit_index}`}
                            onClick={() => doCall(() => api.post(`/lab/orders/${o.id}/units/${u.unit_index}/quality`, { result: "reject", notes: "rework" }))}>{tr("lab.qcReject")}</Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <p className="font-medium text-navy mb-2">{tr("lab.productionTimeline")}</p>
            <div className="space-y-2">
              {o.timeline?.map((t, i) => (
                <div key={i} className="flex items-start gap-2 border-l-2 border-slate-200 pl-3">
                  <div>
                    <p className="text-slate-700">{tr(`lab.stage_${t.stage}`, STAGE_LABEL[t.stage] || t.stage)}{t.note ? ` — ${t.note}` : ""}</p>
                    <p className="text-xs text-slate-400">{(t.at || "").slice(0, 16).replace("T", " ")} · {t.by_name}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InventoryTab({ can }) {
  const t = useT();
  const [mats, setMats] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => { setLoading(true); api.get("/lab/materials").then((r) => setMats(r.data)).finally(() => setLoading(false)); }, []);
  useEffect(() => { load(); }, [load]);
  const showCost = mats.some((m) => "unit_cost" in m);

  const consume = async (m) => {
    try { await api.post("/lab/materials/consume", { material_id: m.id, quantity: 1 }); toast.success(`${t("lab.consumed")}: 1 ${m.unit} · ${m.name}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (loading) return <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-navy" /></div>;
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead><tr className="bg-slate-50 text-left">
          <th className="px-4 py-3">{t("lab.invMaterial")}</th><th className="px-4 py-3">{t("lab.invSku")}</th>
          <th className="px-4 py-3">{t("lab.invStock")}</th><th className="px-4 py-3">{t("lab.invReorder")}</th>
          {showCost && <th className="px-4 py-3">{t("lab.invUnitCost")}</th>}<th className="px-4 py-3">{t("lab.invStatus")}</th><th></th>
        </tr></thead>
        <tbody>
          {mats.map((m) => (
            <tr key={m.id} data-testid={`material-${m.id}`} className="border-t border-slate-100">
              <td className="px-4 py-3 font-medium">{m.name}</td>
              <td className="px-4 py-3 text-slate-500 font-mono">{m.sku || "—"}</td>
              <td className="px-4 py-3 tabular-nums">{m.stock_qty} {m.unit}</td>
              <td className="px-4 py-3 tabular-nums text-slate-500">{m.reorder_level}</td>
              {showCost && <td className="px-4 py-3 tabular-nums">{eur(m.unit_cost)}</td>}
              <td className="px-4 py-3">{m.low_stock ? <Badge variant="destructive" className="gap-1"><AlertTriangle className="h-3 w-3" />{t("lab.lowStock")}</Badge> : <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 font-normal">{t("lab.ok")}</Badge>}</td>
              <td className="px-4 py-3">{can("laboratory", "edit") && <Button size="sm" variant="outline" onClick={() => consume(m)} data-testid={`consume-${m.id}`}>{t("lab.consume")}</Button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LabDashboardTab() {
  const t = useT();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/lab/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-navy" /></div>;
  const Tile = ({ label, value, tone }) => (
    <div className="bg-white rounded-xl border border-slate-200 p-5 stat-lift">
      <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`font-heading text-3xl font-semibold mt-2 tabular-nums ${tone || "text-navy"}`}>{value}</p>
    </div>
  );
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Tile label={t("lab.activeOrders")} value={d.active_orders} />
        <Tile label={t("lab.completedOrders")} value={d.completed_orders} tone="text-emerald-600" />
        <Tile label={t("lab.delayedOrders")} value={d.delayed_orders} tone="text-red-600" />
        <Tile label={t("lab.deadlineConflicts")} value={d.deadline_conflicts} tone="text-amber-600" />
        <Tile label={t("lab.lowStockMaterials")} value={d.low_stock_count} tone="text-amber-600" />
        {"production_costs" in d && <Tile label={t("lab.productionCosts")} value={eur(d.production_costs)} />}
      </div>
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-heading text-lg font-medium text-navy mb-4">{t("lab.ordersPerTech")}</h3>
        <div className="space-y-2">
          {Object.entries(d.orders_per_technician).length === 0 && <p className="text-slate-400 text-sm">{t("lab.noActiveOrders")}</p>}
          {Object.entries(d.orders_per_technician).map(([name, count]) => (
            <div key={name} className="flex items-center justify-between">
              <span className="text-slate-600">{name}</span>
              <span className="font-semibold text-navy tabular-nums">{count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
